import * as Sentry from "@sentry/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useFetcher } from "react-router";
import { usePostalCodeParam } from "~/hooks/use-postal-code-param";
import type { MapCenter } from "./pharmacy-map.types";
import {
	getCachedPostalCodeCenter,
	isPharmacyMapRateLimitError,
	setCachedPostalCodeCenter,
} from "./pharmacy-map-cache";

interface PostalCodeLocationResponse {
	lat?: number | null;
	lng?: number | null;
	notFound?: boolean;
	error?: string;
}

interface PostalCodeCenterResult {
	center: MapCenter | null;
	isLoading: boolean;
	hasError: boolean;
	postalCode: string;
}

interface UsePostalCodeCenterOptions {
	enabled?: boolean;
}

/**
 * A delivery-method switch navigates (MKP-2054: `setVendor` calls
 * `navigate(..., { replace: true })`), which can abort a `fetcher.load()`
 * that was still in flight for the *previous* render's `enabled`/`postalCode`
 * combination. React Router leaves an aborted fetcher parked in `"loading"`
 * rather than resolving it — without this watchdog, `lastFetchedPostalCode`
 * already claims that postal code as fetched, so the retry effect never
 * fires and `center` stays `null` forever (the radius control stays
 * disabled even once the map's own data has loaded).
 */
const STUCK_FETCH_TIMEOUT_MS = 5000;

/**
 * Resolves the current URL postal code to map coordinates.
 * Uses the URL postal code as the single source of truth.
 * Reports unexpected API failures to Sentry (not "not found" cases).
 * Skips all network requests when `enabled` is false.
 * Returns `{ center, isLoading, hasError, postalCode }`.
 */
export function usePostalCodeCenter({
	enabled = true,
}: UsePostalCodeCenterOptions = {}): PostalCodeCenterResult {
	const { postalCode: urlPostalCode } = usePostalCodeParam();
	const postalCode = urlPostalCode ?? "";
	const fetcher = useFetcher<PostalCodeLocationResponse>();
	const lastFetchedPostalCode = useRef("");

	// biome-ignore lint/correctness/useExhaustiveDependencies: fetcher is a new object every render; lastFetchedPostalCode ref prevents duplicate fetches
	useEffect(() => {
		if (
			!enabled ||
			!postalCode ||
			postalCode === lastFetchedPostalCode.current
		) {
			return;
		}
		lastFetchedPostalCode.current = postalCode;
		const params = new URLSearchParams({ postalCode });
		fetcher.load(`/api/postal-code-location?${params}`);
	}, [enabled, postalCode]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: fetcher is a new object every render; only the loading/idle transition should restart the timer
	useEffect(() => {
		if (fetcher.state !== "loading") {
			return;
		}
		const stuckPostalCode = postalCode;
		const timer = setTimeout(() => {
			if (fetcher.state !== "loading") {
				return;
			}
			// Keep the ref pointing at the postal code being retried. Clearing it
			// would leave `isLoading` (which compares the ref to `postalCode`)
			// stuck true for the rest of the session, since the effect above only
			// re-runs when `enabled`/`postalCode` change.
			lastFetchedPostalCode.current = stuckPostalCode;
			const params = new URLSearchParams({ postalCode: stuckPostalCode });
			fetcher.load(`/api/postal-code-location?${params}`);
		}, STUCK_FETCH_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, [fetcher.state, postalCode]);

	const isNotFound =
		fetcher.state === "idle" &&
		fetcher.data != null &&
		fetcher.data.notFound === true;
	const isRateLimited =
		fetcher.state === "idle" &&
		fetcher.data != null &&
		isPharmacyMapRateLimitError(fetcher.data.error);
	const cachedCenter = postalCode
		? getCachedPostalCodeCenter(postalCode)
		: null;
	const hasError =
		fetcher.state === "idle" &&
		fetcher.data != null &&
		fetcher.data.lat == null &&
		!isNotFound &&
		(!isRateLimited || cachedCenter == null);

	useEffect(() => {
		if (fetcher.state !== "idle" || !postalCode || !fetcher.data) {
			return;
		}
		if (fetcher.data.lat != null && fetcher.data.lng != null) {
			setCachedPostalCodeCenter(postalCode, {
				lat: fetcher.data.lat,
				lng: fetcher.data.lng,
			});
			return;
		}
		if (fetcher.data.notFound) {
			setCachedPostalCodeCenter(postalCode, { notFound: true });
		}
	}, [fetcher.state, fetcher.data, postalCode]);

	useEffect(() => {
		if (fetcher.state !== "idle" || !postalCode || !fetcher.data) {
			return;
		}
		if (fetcher.data.notFound) {
			return;
		}
		if (fetcher.data.lat != null && fetcher.data.lng != null) {
			return;
		}
		Sentry.captureMessage("Postal code location lookup failed", {
			level: "warning",
			extra: {
				postalCode,
				responseError: fetcher.data.error,
				feature: "pharmacy-map",
			},
		});
	}, [fetcher.state, fetcher.data, postalCode]);

	const center: MapCenter | null = useMemo(() => {
		if (fetcher.data?.lat != null && fetcher.data?.lng != null) {
			return { lat: fetcher.data.lat, lng: fetcher.data.lng };
		}
		if (
			isRateLimited &&
			cachedCenter?.lat != null &&
			cachedCenter.lng != null &&
			!cachedCenter.notFound
		) {
			return { lat: cachedCenter.lat, lng: cachedCenter.lng };
		}
		return null;
	}, [
		fetcher.data?.lat,
		fetcher.data?.lng,
		isRateLimited,
		cachedCenter?.lat,
		cachedCenter?.lng,
		cachedCenter?.notFound,
	]);

	// Load lives in an effect, so the first paint is idle with no data.
	// Treat that as loading so the map panel does not flash MapNoAddress.
	const isLoading =
		Boolean(enabled && postalCode) &&
		(fetcher.state !== "idle" ||
			fetcher.data == null ||
			lastFetchedPostalCode.current !== postalCode);

	return {
		center,
		isLoading,
		hasError,
		postalCode,
	};
}
